/* eslint-disable prettier/prettier, jsx-a11y/control-has-associated-label */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import {
  Search as SearchIcon,
  GetApp as GetAppIcon,
  DateRange as DateRangeIcon,
  ClearAll as ClearAllIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { toastr } from 'react-redux-toastr';
import * as XLSX from 'xlsx';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingProgress from 'common/components/progress/loading';
import { BASE_API, USER_TOKEN } from 'config/consts';

const SOURCE_CONFIG = {
  link: { label: 'Link', bg: '#eef2ff', fg: '#4338ca' },
  produto: { label: 'Produto', bg: '#e6fbf5', fg: '#0f766e' },
  assinatura: { label: 'Assinatura', bg: '#f3e8ff', fg: '#7e22ce' },
};

const RECURRENCE_LABELS = {
  single: 'Venda única',
  weekly: 'Semanal',
  biweekly: 'Quinzenal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  annual: 'Anual',
};

const STATUS_ENUM_LABEL = {
  paid: 'Pago',
  pending: 'Pendente',
  waiting_payment: 'Aguardando',
  processing: 'Processando',
  expired: 'Expirado',
  canceled: 'Cancelado',
  refunded: 'Estornado',
  refused: 'Recusado',
  unpaid: 'Inadimplente',
  // Estados que só a Core API v5 produz. Sem eles a tela mostraria o termo
  // cru em inglês para o gestor.
  failed: 'Recusado',
  partial_canceled: 'Estornado parcial',
  chargedback: 'Chargeback',
  overpaid: 'Pago a maior',
  underpaid: 'Pago a menor',
};
const NEGATIVE_STATUSES = new Set([
  'canceled',
  'refused',
  'expired',
  'unpaid',
  'refunded',
  'failed',
  'partial_canceled',
  'chargedback',
  'underpaid',
  'Cancelado',
  'Expirado',
  'Estornada',
  'Parcialmente estornada',
  'Pagamento recusado',
]);

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1440, margin: '0 auto', padding: theme.spacing(3) },
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  title: { fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.2 },
  subtitle: { color: '#64748b', fontSize: '0.9rem', marginTop: theme.spacing(0.5) },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    padding: theme.spacing(2, 2.25),
    minWidth: 0,
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 46,
    height: 46,
    borderRadius: 0,
    backgroundColor: '#eef2ff',
    color: '#4338ca',
    flexShrink: 0,
    '& .material-icons': { fontSize: 24 },
  },
  statBody: { minWidth: 0 },
  statLabel: { fontSize: '0.75rem', fontWeight: 500, color: '#64748b', lineHeight: 1.3, marginBottom: 2 },
  statValue: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.15,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  panel: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
  },
  panelLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#94a3b8',
    marginBottom: theme.spacing(2),
  },
  clearButton: { textTransform: 'none', fontWeight: 600, color: '#64748b' },
  actionsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  hint: { fontSize: '0.85rem', color: '#475569' },
  hintStrong: { fontWeight: 700, color: '#0f172a' },
  primaryButton: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: theme.spacing(0.5),
    boxShadow: 'none',
    padding: theme.spacing(1, 2.5),
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 0,
    boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
    overflowX: 'auto',
    '& .p-datatable': { fontSize: '13px', border: 'none' },
    '& .p-datatable-thead > tr > th': {
      backgroundColor: '#f8fafc',
      color: '#475569',
      fontWeight: 600,
      fontSize: '0.72rem',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      padding: theme.spacing(1.5, 1.25),
      borderColor: '#e2e8f0',
      whiteSpace: 'nowrap',
    },
    '& .p-datatable-tbody > tr > td': {
      padding: theme.spacing(1.25, 1.25),
      borderTop: '1px solid #f1f5f9',
      color: '#1e293b',
      fontSize: '0.85rem',
      whiteSpace: 'nowrap',
    },
    '& .p-datatable-tbody > tr:nth-of-type(even)': { backgroundColor: '#fbfcfe' },
    '& .p-datatable-tbody > tr:hover': { backgroundColor: '#eef2ff !important' },
    '& .p-paginator': {
      borderTop: '1px solid #e2e8f0',
      padding: theme.spacing(0.5, 1.5),
      backgroundColor: '#ffffff',
    },
    '& .p-paginator .p-dropdown': { borderRadius: 0, border: '1px solid #cbd5f5', backgroundColor: '#f8fafc' },
  },
  money: { fontVariantNumeric: 'tabular-nums', fontWeight: 600 },
  moneyMuted: { fontVariantNumeric: 'tabular-nums', color: '#94a3b8' },
  customerPrimary: { color: '#1e293b', lineHeight: 1.3 },
  customerSecondary: { color: '#94a3b8', fontSize: '0.75rem', lineHeight: 1.3 },
  chip: { fontWeight: 600, fontSize: '0.72rem', height: 22, borderRadius: 0 },
  emptyState: { padding: theme.spacing(8, 3), textAlign: 'center', color: '#64748b' },
  emptyIcon: { fontSize: 48, color: '#cbd5e1', marginBottom: theme.spacing(1) },
}));

const currentMonthRange = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const first = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastStr = `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
  return { start: first, end: lastStr };
};

const PaymentLinksReport = ({ auth }) => {
  const classes = useStyles();
  const initialRange = useMemo(currentMonthRange, []);

  const authUser = auth?.user;
  const station = authUser?.establishments?.[0]?.stations?.[0] || {};
  const stationId = station.id || null;
  const stationName = station.name || 'Minha entidade';

  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [paidOnly, setPaidOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData] = useState({ list: [], metrics: null, total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const requestSeq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = useCallback(
    (overrides = {}) => ({
      startDate,
      endDate,
      source: sourceFilter,
      paidOnly: paidOnly ? 'true' : 'false',
      search: debouncedSearch,
      page,
      pageSize,
      ...overrides,
    }),
    [startDate, endDate, sourceFilter, paidOnly, debouncedSearch, page, pageSize]
  );

  const fetchSales = useCallback(async () => {
    if (!stationId) return;
    const seq = (requestSeq.current += 1);
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/sales-report/station/${stationId}`, {
        params: buildParams(),
        headers: { Authorization: `${token}` },
      });
      if (seq !== requestSeq.current) return;
      setData({
        list: response.data.list || [],
        metrics: response.data.metrics || null,
        total: response.data.total || 0,
      });
    } catch (error) {
      if (seq === requestSeq.current) {
        toastr.error('Erro', 'Não foi possível carregar o relatório de vendas.');
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [stationId, buildParams]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, sourceFilter, paidOnly, debouncedSearch, pageSize]);

  const activeFilterCount =
    (sourceFilter !== 'all' ? 1 : 0) + (paidOnly ? 1 : 0) + (debouncedSearch ? 1 : 0);

  const clearFilters = () => {
    setSourceFilter('all');
    setPaidOnly(false);
    setSearch('');
    setDebouncedSearch('');
    setStartDate(initialRange.start);
    setEndDate(initialRange.end);
  };

  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
  };

  const mapPaymentMethod = (method) => {
    if (!method) return '—';
    const n = method.toLowerCase();
    if (n === 'pix') return 'PIX';
    if (n.includes('credit') || n.includes('crédito') || n === 'cartao') return 'Cartão de crédito';
    if (n === 'boleto') return 'Boleto';
    return method;
  };

  const statusLabel = (row) => STATUS_ENUM_LABEL[row.status] || row.status || '—';
  const statusStyle = (row) => {
    if (Number(row.is_paid)) return { backgroundColor: '#e6f4ea', color: '#1b7a3d' };
    if (NEGATIVE_STATUSES.has(row.status)) return { backgroundColor: '#fdecea', color: '#b3261e' };
    return { backgroundColor: '#fff8e1', color: '#8d6e00' };
  };

  const metrics = data.metrics;
  const kpiCards = [
    { icon: 'shopping_cart', label: 'Vendas no período', value: metrics ? metrics.totalCount : 0 },
    { icon: 'check_circle', label: 'Vendas pagas', value: metrics ? metrics.paidCount : 0 },
    {
      icon: 'payments',
      label: 'Bruto faturado',
      value: formatCurrency(metrics ? metrics.grossPaid : 0),
    },
    {
      icon: 'account_balance_wallet',
      label: 'Líquido faturado',
      value: formatCurrency(metrics ? metrics.netPaid : 0),
    },
  ];

  const tableRows = useMemo(
    () =>
      data.list.map((row, idx) => ({
        ...row,
        rowId: `${row.source}:${row.sale_ref}:${row.cycle_number || ''}:${idx}`,
      })),
    [data.list]
  );

  const bySourceSummary = useMemo(() => {
    if (!metrics?.bySource) return [];
    return Object.entries(metrics.bySource).map(([key, v]) => ({
      key,
      label: SOURCE_CONFIG[key]?.label || key,
      count: v.count,
      paidCount: v.paidCount,
    }));
  }, [metrics]);

  const sourceBody = (row) => {
    const cfg = SOURCE_CONFIG[row.source] || { label: row.source, bg: '#eef2ff', fg: '#4338ca' };
    return (
      <Chip label={cfg.label} size="small" className={classes.chip} style={{ backgroundColor: cfg.bg, color: cfg.fg }} />
    );
  };
  const dateBody = (row) => formatDate(row.sale_date);
  const customerBody = (row) => (
    <Box>
      <div className={classes.customerPrimary}>{row.customer_name || '—'}</div>
      {row.cpf ? <div className={classes.customerSecondary}>{row.cpf}</div> : null}
    </Box>
  );
  const grossBody = (row) => <span className={classes.money}>{formatCurrency(row.gross)}</span>;
  const netBody = (row) =>
    row.net === null || row.net === undefined ? (
      <span className={classes.moneyMuted}>—</span>
    ) : (
      <span className={classes.money}>{formatCurrency(row.net)}</span>
    );
  const methodBody = (row) => mapPaymentMethod(row.payment_method);
  const statusBody = (row) => (
    <Chip label={statusLabel(row)} size="small" className={classes.chip} style={statusStyle(row)} />
  );
  const recurrenceBody = (row) => {
    if (!Number(row.is_recurring)) return <span style={{ color: '#94a3b8' }}>—</span>;
    const label = RECURRENCE_LABELS[row.recurrence] || row.recurrence || 'Recorrente';
    return (
      <span style={{ color: '#4338ca', fontWeight: 600 }}>
        {label}
        {row.cycle_number ? ` · #${row.cycle_number}` : ''}
      </span>
    );
  };

  const emptyBody = () => (
    <div className={classes.emptyState}>
      <Icon className={classes.emptyIcon}>{activeFilterCount ? 'filter_alt_off' : 'receipt_long'}</Icon>
      <Typography style={{ fontWeight: 600, color: '#475569' }}>
        {activeFilterCount ? 'Nenhuma venda com os filtros atuais' : 'Nenhuma venda no período'}
      </Typography>
      <Typography variant="body2" style={{ marginTop: 4 }}>
        {debouncedSearch && (startDate || endDate)
          ? 'A busca considera apenas o período selecionado. Limpe as datas para buscar em todo o histórico.'
          : activeFilterCount
          ? 'Ajuste ou limpe os filtros para ver mais resultados.'
          : 'Altere o período para encontrar vendas anteriores.'}
      </Typography>
    </div>
  );

  const handleExport = async () => {
    if (!stationId) return;
    if (!data.total) {
      toastr.warning('Nada para exportar', 'Não há vendas no período/filtragem atual.');
      return;
    }
    setExporting(true);
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/sales-report/station/${stationId}`, {
        params: buildParams({ page: 0, pageSize: 10000 }),
        headers: { Authorization: `${token}` },
      });
      const all = response.data.list || [];
      const rows = all.map((row) => ({
        Fonte: SOURCE_CONFIG[row.source]?.label || row.source,
        Referência: row.sale_ref,
        Data: formatDate(row.sale_date),
        Cliente: row.customer_name || '',
        CPF: row.cpf || '',
        'E-mail': row.email || '',
        Telefone: row.phone || '',
        Descrição: row.description || '',
        'Valor bruto': Number(row.gross || 0),
        'Valor líquido': row.net === null || row.net === undefined ? '' : Number(row.net),
        'Forma de pagamento': mapPaymentMethod(row.payment_method),
        Status: statusLabel(row),
        Pago: Number(row.is_paid) ? 'Sim' : 'Não',
        Recorrente: Number(row.is_recurring) ? 'Sim' : 'Não',
        Recorrência: Number(row.is_recurring)
          ? RECURRENCE_LABELS[row.recurrence] || row.recurrence || ''
          : '',
        Ciclo: row.cycle_number || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Vendas');
      const fileName = `Relatorio_Vendas_${startDate}_a_${endDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
      if (all.length >= 10000) {
        toastr.warning('Exportação parcial', 'O período tem mais de 10.000 vendas; refine o período para exportar tudo.');
      } else {
        toastr.success('Exportado', `${all.length} venda(s) exportada(s).`);
      }
    } catch (error) {
      toastr.error('Erro', 'Falha ao exportar o relatório.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box className={classes.page}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>Relatório de Vendas</Typography>
          <Typography className={classes.subtitle}>
            {stationName} · todas as vendas (links, pedidos e assinaturas) processadas na plataforma
          </Typography>
        </div>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          startIcon={<GetAppIcon />}
          onClick={handleExport}
          disabled={exporting || !data.total}>
          {exporting ? 'Exportando…' : 'Exportar Excel'}
        </Button>
      </div>

      <div className={classes.statGrid}>
        {kpiCards.map((kpi) => (
          <div className={classes.statCard} key={kpi.label}>
            <div className={classes.statIcon}>
              <Icon>{kpi.icon}</Icon>
            </div>
            <div className={classes.statBody}>
              <div className={classes.statLabel}>{kpi.label}</div>
              <div className={classes.statValue} title={String(kpi.value)}>
                {kpi.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Box className={classes.panel}>
        <div className={classes.panelLabel}>
          <DateRangeIcon style={{ fontSize: 16 }} />
          Filtros
          {activeFilterCount > 0 && (
            <Chip
              label={`${activeFilterCount} ativo${activeFilterCount > 1 ? 's' : ''}`}
              size="small"
              style={{ height: 20, borderRadius: 0, backgroundColor: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
            />
          )}
          <Box flexGrow={1} />
          {activeFilterCount > 0 && (
            <Button size="small" className={classes.clearButton} startIcon={<ClearAllIcon />} onClick={clearFilters}>
              Limpar
            </Button>
          )}
        </div>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar por cliente, CPF, referência ou descrição"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Fonte"
              SelectProps={{ native: true }}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}>
              <option value="all">Todas as fontes</option>
              <option value="link">Links de pagamento</option>
              <option value="produto">Pedidos de produto</option>
              <option value="assinatura">Assinaturas</option>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              variant="outlined"
              label="Data início"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              variant="outlined"
              label="Data fim"
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <FormControlLabel
              control={<Switch color="primary" checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)} />}
              label={<span style={{ fontSize: '0.85rem' }}>Só pagas</span>}
            />
          </Grid>
        </Grid>

        {bySourceSummary.length > 0 && (
          <Box mt={2} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
            {bySourceSummary.map((s) => (
              <Chip
                key={s.key}
                size="small"
                className={classes.chip}
                style={{ backgroundColor: SOURCE_CONFIG[s.key]?.bg, color: SOURCE_CONFIG[s.key]?.fg }}
                label={`${s.label}: ${s.count} (${s.paidCount} pagas)`}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box className={classes.tableContainer} style={{ position: 'relative' }}>
        {loading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={2}
            style={{ background: 'rgba(255,255,255,0.6)' }}>
            <LoadingProgress />
          </Box>
        )}
        <DataTable
          value={tableRows}
          dataKey="rowId"
          lazy
          paginator
          first={page * pageSize}
          rows={pageSize}
          totalRecords={data.total}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPage={(e) => {
            setPage(e.page);
            setPageSize(e.rows);
          }}
          emptyMessage={emptyBody()}
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} vendas"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown">
          <Column header="Fonte" body={sourceBody} style={{ minWidth: 110 }} />
          <Column header="Data" body={dateBody} style={{ minWidth: 100 }} />
          <Column header="Cliente" body={customerBody} style={{ minWidth: 200 }} />
          <Column field="description" header="Descrição" style={{ minWidth: 220 }} />
          <Column
            header="Valor"
            body={grossBody}
            style={{ minWidth: 110 }}
            bodyStyle={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column
            header="Líquido"
            body={netBody}
            style={{ minWidth: 110 }}
            bodyStyle={{ textAlign: 'right' }}
            headerStyle={{ textAlign: 'right' }}
          />
          <Column header="Pagamento" body={methodBody} style={{ minWidth: 140 }} />
          <Column header="Status" body={statusBody} style={{ minWidth: 120 }} />
          <Column header="Recorrência" body={recurrenceBody} style={{ minWidth: 150 }} />
        </DataTable>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(PaymentLinksReport);
